import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  Video, Plus, Loader, RefreshCw,
} from "lucide-react";
import {
  lmsGetLiveClasses, lmsSyncLiveClassStatus, lmsCreateLiveClass, lmsUpdateLiveClass,
  lmsDeleteLiveClass, lmsGetTeacherByUserId, lmsGetTeacherClasses,
  lmsGetBatches, lmsGetClassSubjects,
} from "../api/lmsApi";
import { ScheduleModal, JoinModal, LiveClassCard, ConfirmModal } from "./LiveClasses";
import "../lms.css";

const getTeacherData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); }
  catch { return {}; }
};

export default function TeacherLiveClasses() {
  const userData  = getTeacherData();
  const adminId   = userData?.admin_id || null;
  const schoolId  = userData?.school_id || userData?.admin_id || null;
  const [resolvedTeacherId, setResolvedTeacherId] = useState(userData?.teacher_id || null);
  const [teacherClasses,    setTeacherClasses]    = useState([]);

  const [classes,       setClasses]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [filter,        setFilter]        = useState("all");
  const [showSchedule,  setShowSchedule]  = useState(false);
  const [editItem,      setEditItem]      = useState(null);
  const [joinItem,      setJoinItem]      = useState(null);
  const [confirmDel,    setConfirmDel]    = useState(null);
  const [notifDenied,   setNotifDenied]   = useState(false);
  const [notifDefault,  setNotifDefault]  = useState(false);

  // Advanced filters
  const [filterClass,    setFilterClass]    = useState("");
  const [filterBatch,    setFilterBatch]    = useState("");
  const [filterSubject,  setFilterSubject]  = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo,   setFilterDateTo]   = useState("");
  const [filterSubjects, setFilterSubjects] = useState([]);
  const [filterBatches,  setFilterBatches]  = useState([]);

  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "denied") setNotifDenied(true);
    else if (Notification.permission === "default") setNotifDefault(true);
  }, []);

  const handleEnableNotifications = () => {
    Notification.requestPermission().then((p) => {
      if (p === "granted") setNotifDefault(false);
      else if (p === "denied") { setNotifDefault(false); setNotifDenied(true); }
    });
  };

  // Resolve teacher ID then load their assigned classes for filter dropdown
  useEffect(() => {
    const tid = userData?.teacher_id;
    const setup = (id) => {
      setResolvedTeacherId(id);
      lmsGetTeacherClasses(id)
        .then((res) => setTeacherClasses(res.data?.data || []))
        .catch(() => setTeacherClasses([]));
    };
    if (tid) {
      setup(tid);
    } else if (adminId) {
      lmsGetTeacherByUserId(adminId)
        .then((res) => { if (res?.data?.id) setup(res.data.id); })
        .catch(() => {});
    }
  }, [adminId]);

  useEffect(() => {
    setFilterBatch("");    setFilterBatches([]);
    setFilterSubject("");  setFilterSubjects([]);
    if (!filterClass) return;
    lmsGetClassSubjects({ class_id: filterClass, school_id: schoolId })
      .then((r) => setFilterSubjects(r.data?.data || r.data || []))
      .catch(() => {});
    lmsGetBatches({ class_id: filterClass, school_id: schoolId })
      .then((r) => setFilterBatches(r.data?.data || r.data || []))
      .catch(() => {});
  }, [filterClass, schoolId]);

  // Load live classes — backend filters by teacher_id so only assigned classes are returned
  const load = useCallback(async () => {
    if (!resolvedTeacherId) return;
    setLoading(true);
    try {
      lmsSyncLiveClassStatus().catch(() => {});
      const params = { school_id: schoolId, teacher_id: resolvedTeacherId };
      const res = await lmsGetLiveClasses(params);
      const all = res.data.data || [];
      const displayed = filter === "all" ? all : all.filter((lc) => lc.status === filter);
      setClasses(displayed);
    } catch { toast.error("Failed to load live classes"); }
    finally { setLoading(false); }
  }, [schoolId, resolvedTeacherId, filter]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (editItem) {
      await lmsUpdateLiveClass(editItem.id, { ...form, school_id: schoolId });
      toast.success("Class updated");
    } else {
      await lmsCreateLiveClass({ ...form, school_id: schoolId, teacher_id: resolvedTeacherId });
      toast.success("Class scheduled!");
    }
    setEditItem(null);
    load();
  };

  const handleDelete = async () => {
    try {
      await lmsDeleteLiveClass(confirmDel.id);
      toast.success("Class deleted");
      setConfirmDel(null);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const filteredClasses = classes.filter((c) => {
    if (filterClass   && String(c.class_id)   !== String(filterClass))   return false;
    if (filterBatch   && String(c.batch_id)   !== String(filterBatch))   return false;
    if (filterSubject && String(c.subject_id) !== String(filterSubject)) return false;
    if (filterDateFrom && new Date(c.scheduled_at) < new Date(filterDateFrom)) return false;
    if (filterDateTo) {
      const toEnd = new Date(filterDateTo); toEnd.setHours(23, 59, 59, 999);
      if (new Date(c.scheduled_at) > toEnd) return false;
    }
    return true;
  });

  const hasFilters = filterClass || filterBatch || filterSubject || filterDateFrom || filterDateTo;
  const clearFilters = () => {
    setFilterClass(""); setFilterBatch(""); setFilterSubject("");
    setFilterDateFrom(""); setFilterDateTo("");
  };

  const liveCount      = classes.filter((c) => c.status === "live").length;
  const scheduledCount = classes.filter((c) => c.status === "scheduled").length;

  return (
    <div className="lms-content-inner">
      {notifDefault && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
          background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: 10,
          marginBottom: 16, fontSize: 13, color: "#1e40af",
        }}>
          <span style={{ fontSize: 16 }}>🔔</span>
          <span style={{ flex: 1 }}>
            <strong>Enable notifications</strong> to get alerts when students join the waiting room.
          </span>
          <button onClick={handleEnableNotifications} style={{
            padding: "6px 14px", borderRadius: 7, border: "none",
            background: "#2563eb", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}>Allow</button>
        </div>
      )}
      {notifDenied && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
          background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10,
          marginBottom: 16, fontSize: 13, color: "#92400e",
        }}>
          <span style={{ fontSize: 16 }}>🔔</span>
          <span style={{ flex: 1 }}>
            <strong>Notifications blocked.</strong> Go to browser Site Settings → Notifications → Allow.
          </span>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: "#111827" }}>My Live Classes</div>
        </div>
        {liveCount > 0 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#16a34a" }}>{liveCount}</div>
            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>LIVE NOW</div>
          </div>
        )}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#4f46e5" }}>{scheduledCount}</div>
          <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>UPCOMING</div>
        </div>
        <button onClick={() => { setEditItem(null); setShowSchedule(true); }} style={{
          display: "flex", alignItems: "center", gap: 7, padding: "10px 18px",
          borderRadius: 10, border: "none",
          background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
          color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
        }}>
          <Plus size={15} /> Schedule Class
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[
          { val: "all",       label: "All Classes" },
          { val: "live",      label: "🔴 Live Now" },
          { val: "scheduled", label: "Upcoming" },
          { val: "ended",     label: "Ended" },
        ].map(({ val, label }) => (
          <button key={val} onClick={() => setFilter(val)} style={{
            padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: `1.5px solid ${filter === val ? "#4f46e5" : "#e5e7eb"}`,
            background: filter === val ? "#4f46e5" : "#fff",
            color: filter === val ? "#fff" : "#6b7280",
            cursor: "pointer",
          }}>{label}</button>
        ))}
        <button onClick={load} style={{
          marginLeft: "auto", padding: "7px 12px", borderRadius: 8,
          border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280",
          cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontSize: 13,
        }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Advanced Filters */}
      <div className="crs-filters" style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <select className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 110 }}
          value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
          <option value="">All Classes</option>
          {teacherClasses.map((c) => (
            <option key={c.class_id} value={String(c.class_id)}>{c.class_name}</option>
          ))}
        </select>

        <select className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 110, opacity: filterClass ? 1 : 0.5 }}
          value={filterBatch} onChange={(e) => setFilterBatch(e.target.value)} disabled={!filterClass}>
          <option value="">All Batches</option>
          {filterBatches.map((b) => (
            <option key={b.id} value={String(b.id)}>{b.name}{b.section ? ` (${b.section})` : ""}</option>
          ))}
        </select>

        <select className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 110, opacity: filterClass ? 1 : 0.5 }}
          value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)} disabled={!filterClass}>
          <option value="">All Subjects</option>
          {filterSubjects.map((s) => (
            <option key={s.subject_id ?? s.id} value={String(s.subject_id ?? s.id)}>{s.subject_name ?? s.name}</option>
          ))}
        </select>

        <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>From</span>
        <input type="date" className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 130 }}
          value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />

        <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>To</span>
        <input type="date" className="crs-filter-select" style={{ flex: "1 1 0", width: 0, minWidth: 130 }}
          value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />

        {hasFilters && (
          <>
            <button onClick={clearFilters} style={{
              padding: "7px 14px", borderRadius: 8, border: "1px solid #fca5a5",
              background: "#fff7f7", color: "#ef4444", fontSize: 13, fontWeight: 600,
              cursor: "pointer", whiteSpace: "nowrap",
            }}>✕ Clear</button>
            <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
              {filteredClasses.length} of {classes.length} classes
            </span>
          </>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Loader size={28} className="ms2-spin" color="#4f46e5" />
        </div>
      ) : filteredClasses.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb",
        }}>
          <Video size={48} color="#e5e7eb" style={{ margin: "0 auto 16px" }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
            {hasFilters ? "No classes match the selected filters" : filter === "live" ? "No live sessions right now" : "No classes found"}
          </div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
            Schedule a live session for your students.
          </div>
          <button onClick={() => { setEditItem(null); setShowSchedule(true); }} style={{
            padding: "10px 24px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>+ Schedule First Class</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16 }}>
          {filteredClasses.map((item) => (
            <LiveClassCard
              key={item.id}
              item={item}
              isAdminOrTeacher
              onJoin={() => setJoinItem(item)}
              onEdit={(i) => { setEditItem(i); setShowSchedule(true); }}
              onDelete={setConfirmDel}
              onRefreshStatus={load}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showSchedule && (
        <ScheduleModal
          onClose={() => { setShowSchedule(false); setEditItem(null); }}
          onSave={handleSave}
          schoolClasses={teacherClasses}
          editItem={editItem}
          schoolId={schoolId}
        />
      )}
      {joinItem && (
        <JoinModal liveClass={joinItem} onClose={() => setJoinItem(null)} teacherMode />
      )}
      {confirmDel && (
        <ConfirmModal
          message={<>Delete <strong>{confirmDel.title}</strong>? This will also end the BBB meeting.</>}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  );
}
